import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Plus, Users, Shield, ArrowLeft, Lightbulb } from 'lucide-react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Form } from '../common/Form';
import Dialog from '../common/Dialog';
import { useToast, ToastContainer } from '../common/Toast';
import userService, { Role } from '../../services/users';
import sitesService from '../../services/sites';
import authService from '../../services/auth';
import tenantService from '../../services/tenantService';
import { UserProfile } from '../../types/auth';
import CreateUserFormContent from './CreateUserFormContent';

// Schéma de validation pour la création d'utilisateur
const createUserSchema = z.object({
  username: z.string()
    .min(5, 'Le nom d\'utilisateur doit contenir au moins 5 caractères')
    .regex(/^[a-zA-Z0-9.]+$/, 'Le nom d\'utilisateur ne peut contenir que des lettres, chiffres et points')
    .regex(/^[a-zA-Z0-9].*[a-zA-Z0-9]$/, 'Le nom d\'utilisateur ne peut pas commencer ou finir par un point')
    .regex(/^[^\-]+$/, 'Le nom d\'utilisateur ne peut pas contenir de tirets')
    .refine(() => {
      // La structure suggérée n'est plus obligatoire
      // On accepte n'importe quel format valide
      return true;
    }, 'Format de nom d\'utilisateur invalide'),
  email: z.string().email('Email invalide').optional().or(z.literal('')),
  prenom: z.string().min(2, 'Le prénom doit contenir au moins 2 caractères'),
  nom: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
  phone: z.string().optional(),
  locale: z.string().min(2, 'La langue est requise'),
  role_id: z.string().min(1, 'Le rôle est requis'),
  tenant_id: z.string().optional(),
  default_site_id: z.string().optional()
});

type CreateUserFormData = z.infer<typeof createUserSchema> & {
  name?: string; // Ajouté pour la compatibilité avec le service
};

const CreateUser: React.FC = () => {
  const navigate = useNavigate();
  const { toasts, showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
  const [filteredSites, setFilteredSites] = useState<any[]>([]);
  const [allTenants, setAllTenants] = useState<any[]>([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [createdUserData, setCreatedUserData] = useState<{
    user: any;
    tempPassword?: string;
    emailSent?: boolean;
  } | null>(null);
  
  // États pour la gestion des rôles et permissions
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null);
  const [isTenantUser, setIsTenantUser] = useState(false);
  const [isSiteUser, setIsSiteUser] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  
  // États pour la gestion des champs conditionnels
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [sitesForSelectedTenant, setSitesForSelectedTenant] = useState<any[]>([]);


  useEffect(() => {
    checkUserRole();
  }, []);
  
 
  // Reconfigurer les champs quand le profil utilisateur change
  useEffect(() => {
    if (currentUserProfile && allTenants.length > 0) {
      configureFieldsBasedOnUserRole(allTenants, filteredSites);
    }
  }, [currentUserProfile, allTenants, filteredSites]);

  // Fonction pour vérifier le rôle de l'utilisateur connecté
  const checkUserRole = async () => {
    try {
      // Récupérer le profil utilisateur
      const profile = await authService.getUserProfile();
      setCurrentUserProfile(profile);
      
      // Déterminer le type d'utilisateur basé sur le rôle du profil
      const roleName = profile.role?.name;
      
      if (roleName === 'PLATFORM_SUPER_ADMIN' || roleName === 'PLATFORM_ADMIN' || roleName === 'PLATFORM_SUPPORT') {
        setIsPlatformAdmin(true);
        setIsTenantUser(false);
        setIsSiteUser(false);
      } else if (roleName === 'TENANT_OWNER' || roleName === 'TENANT_ADMIN' || roleName === 'TENANT_MANAGER') {
        setIsPlatformAdmin(false);
        setIsTenantUser(true);
        setIsSiteUser(false);
      } else if (roleName === 'SITE_ADMIN' || roleName === 'SITE_MANAGER' || roleName === 'SITE_SUPERVISOR') {
        setIsPlatformAdmin(false);
        setIsTenantUser(false);
        setIsSiteUser(true);
      } else {
        setIsPlatformAdmin(false);
        setIsTenantUser(false);
        setIsSiteUser(false);
      }
      // Charger les données après avoir déterminé le rôle
      await loadInitialData();
      
    } catch (error) {
      showError('Erreur', 'Impossible de vérifier les permissions');
    }
  };

  const loadInitialData = async () => {
    console.log('🚀 loadInitialData DÉBUT');
    try {
      const [sitesData, rolesData, tenantsData] = await Promise.all([
        sitesService.getSites(),
        userService.getRoles(),
        tenantService.getTenants()
      ]);
      
      const allSites = sitesData || [];
      const allRoles = Array.isArray(rolesData) ? rolesData : rolesData?.results || [];
      const allTenantsList = tenantsData || [];   
      setAllTenants(allTenantsList);


      // Filtrer les rôles selon le rôle de l'utilisateur connecté
      const filteredRolesList = filterRolesByUserRole(allRoles);
      
      // TEMPORAIRE : Si aucun rôle filtré, afficher tous les rôles pour debug
      if (filteredRolesList.length === 0 && allRoles.length > 0) {
        setFilteredRoles(allRoles);
      } else {
        setFilteredRoles(filteredRolesList);
      }
      
      // Pour les administrateurs de plateforme, garder tous les sites pour permettre le filtrage par tenant
      // Pour les autres utilisateurs, filtrer selon leur rôle
      let sitesToUse = allSites;
      if (!isPlatformAdmin && currentUserProfile) {
        sitesToUse = filterSitesByUserRole(allSites);
      } else if (!currentUserProfile) {
        // Si pas de profil utilisateur encore, garder tous les sites temporairement
        sitesToUse = allSites;
      }
      setFilteredSites(sitesToUse);
      
      // Configurer les champs selon le rôle de l'utilisateur connecté
      configureFieldsBasedOnUserRole(allTenantsList, allSites);
      
    } catch (error) {
      showError('Erreur', 'Impossible de charger les données initiales');
      // Initialiser avec des tableaux vides en cas d'erreur
      setFilteredRoles([]);
      setFilteredSites([]);
      setAllTenants([]);
    }
  };

  // Fonction pour configurer les champs selon le rôle de l'utilisateur connecté
  const configureFieldsBasedOnUserRole = (_allTenants: any[], allSites: any[]) => {
    const userRole = currentUserProfile?.role?.name;
    
    // Déterminer si l'utilisateur connecté est un administrateur de plateforme
    const isPlatformAdminUser = userRole && ['PLATFORM_SUPER_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_ADMIN'].includes(userRole);
    setIsPlatformAdmin(isPlatformAdminUser || false);
    
    if (isPlatformAdminUser) {
      setSelectedTenantId('');
      setSitesForSelectedTenant([]);
    } else if (userRole && ['TENANT_ADMIN', 'TENANT_MANAGER', 'TENANT_OWNER'].includes(userRole)) {
      const firstSite = currentUserProfile?.sites?.[0] as any;
      if (firstSite?.tenant?.id) {
        setSelectedTenantId(firstSite.tenant.id.toString());
        const tenantSites = allSites.filter(site => site.tenant_id === firstSite.tenant.id);
        setSitesForSelectedTenant(tenantSites);
      }
    } else if (userRole && ['SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR'].includes(userRole)) {
      const firstSite = currentUserProfile?.sites?.[0] as any;
      if (firstSite?.tenant?.id) {
        setSelectedTenantId(firstSite.tenant.id.toString());
        // Filtrer les sites assignés à l'utilisateur
        const assignedSiteIds = currentUserProfile?.sites?.map(site => site.id) || [];
        const userSites = allSites.filter(site => assignedSiteIds.includes(site.id));
        setSitesForSelectedTenant(userSites);
      }
    } else if (userRole && ['ADMIN', 'MANAGER'].includes(userRole)) {
      const firstSite = currentUserProfile?.sites?.[0] as any;
      if (firstSite?.tenant?.id) {
        setSelectedTenantId(firstSite.tenant.id.toString());
        // Pré-sélectionner le site par défaut de l'utilisateur
        const defaultSite = currentUserProfile?.sites?.find(site => site.is_default);
        if (defaultSite) {
          setSitesForSelectedTenant([defaultSite]);
        }
      }
    } else {
      // Autres rôles : configuration par défaut
      setSelectedTenantId('');
      setSitesForSelectedTenant([]);
    }
  };

  // Fonction pour filtrer les rôles selon le rôle de l'utilisateur connecté
  const filterRolesByUserRole = (allRoles: Role[]): Role[] => {
    if (!currentUserProfile) {
      return [];
    }

    const userRole = currentUserProfile?.role?.name;
    // Rôles autorisés selon le rôle de l'utilisateur connecté
    let allowedRoles: string[] = [];
    if (userRole === 'PLATFORM_SUPER_ADMIN') {
      // Tous les autres rôles sauf PLATFORM_SUPER_ADMIN
      allowedRoles = [
        'PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_MANAGER',
        'SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR', 'STORE_MANAGER', 'VIEWER', 'ADMIN', 'MANAGER', 'OPERATOR'
      ];
    } else if (['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'].includes(userRole || '')) {
      allowedRoles = [
        'SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR', 'STORE_MANAGER', 'TENANT_ADMIN', 'TENANT_MANAGER', 'TENANT_OWNER',
        'VIEWER', 'ADMIN', 'MANAGER', 'OPERATOR'
      ];
    } else if (userRole === 'TENANT_OWNER') {
      allowedRoles = [
        'SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR', 'STORE_MANAGER', 'TENANT_ADMIN', 'TENANT_MANAGER',
        'VIEWER', 'ADMIN', 'MANAGER', 'OPERATOR'
      ];
    } else if (['TENANT_ADMIN', 'TENANT_MANAGER'].includes(userRole || '')) {
      allowedRoles = [
        'SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR', 'STORE_MANAGER', 'VIEWER', 'ADMIN', 'MANAGER', 'OPERATOR'
      ];
    } else if (userRole === 'SITE_ADMIN') {
      allowedRoles = [
        'SITE_MANAGER', 'SITE_SUPERVISOR', 'STORE_MANAGER', 'VIEWER', 'ADMIN', 'MANAGER', 'OPERATOR'
      ];
    } else {
      // Pour les autres rôles ou utilisateurs sans rôle spécifique, permettre seulement les rôles de base
      allowedRoles = [
        'VIEWER', 'ADMIN', 'MANAGER', 'OPERATOR'
      ];
    }
    
    const filteredRoles = allRoles.filter(role => allowedRoles.includes(role.name));
   
    return filteredRoles;
  };

  // Fonction pour filtrer les sites selon le rôle de l'utilisateur connecté
  const filterSitesByUserRole = (allSites: any[]): any[] => { 
    console.log(allSites);
    
    if (!currentUserProfile) {
      console.log('❌ Pas de profil utilisateur');
      return [];
    }

    if (isPlatformAdmin) {
      return allSites;
    }

    if (isTenantUser && currentUserProfile?.sites?.[0]) {
      const firstSite = currentUserProfile.sites[0] as any;
      const tenantSites = allSites.filter(site => site.tenant?.id === firstSite.tenant.id);
      console.log('✅ Utilisateur tenant - sites filtrés:', {
        tenantId: firstSite.tenant.id,
        sitesCount: tenantSites.length
      });
      return tenantSites;
    }

    if (isSiteUser && currentUserProfile?.sites) {
      // Les utilisateurs de site voient seulement leurs sites assignés
      const assignedSiteIds = currentUserProfile.sites.map(site => site.id);
      const userSites = allSites.filter(site => assignedSiteIds.includes(site.id));
      console.log('✅ Utilisateur site - sites assignés:', {
        assignedSiteIds,
        sitesCount: userSites.length
      });
      return userSites;
    }
    return allSites;
  };

  const handleSubmit = async (data: CreateUserFormData) => {
    try {
      setIsLoading(true);
      // Vérifier si un site par défaut est requis selon le rôle sélectionné
      const selectedRole = filteredRoles.find(role => role.id === data.role_id);
      if (selectedRole && isSiteRequiredForRole(selectedRole.name) && !data.default_site_id) {
        showError('Erreur', `Un site par défaut est requis pour le rôle "${getRoleDisplayName(selectedRole)}"`);
        return;
      }
      const userRole = currentUserProfile?.role?.name;
      const isPlatformAdminUser = userRole && ['PLATFORM_SUPER_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_ADMIN'].includes(userRole);
      
      // Vérifier si le rôle sélectionné nécessite un tenant
      const selectedRoleForTenant = filteredRoles.find(role => role.id === data.role_id);
      const showTenantFieldForRole = selectedRoleForTenant && isTenantRequiredForRole(selectedRoleForTenant.name);
      const showTenantFieldConditional = isPlatformAdminUser && showTenantFieldForRole;
      
      if (showTenantFieldConditional && !data.tenant_id) {
        showError('Erreur', 'Le tenant est obligatoire');
        return;
      }
      
      // Reconstituer le nom complet à partir du prénom et du nom
      const userDataWithFullName = {
        ...data,
        name: `${data.prenom} ${data.nom}`.trim(),
        role_id: data.role_id, // Ajouter le role_id pour l'assignation globale
        sites: data.default_site_id ? [{ site_id: data.default_site_id, role_id: data.role_id }] : [],
        // Inclure le tenant_id si disponible (soit depuis le champ, soit depuis l'utilisateur connecté)
        tenant_id: data.tenant_id || selectedTenantId || (currentUserProfile?.sites?.[0] as any)?.tenant?.id
      };
      
      console.log('🔍 Données envoyées au backend:', {
        userData: userDataWithFullName,
        default_site_id: data.default_site_id,
        role_id: data.role_id,
        tenant_id: data.tenant_id,
        selectedTenantId,
        sites: userDataWithFullName.sites
      });
      
      const result = await userService.createUser(userDataWithFullName);
      
      // Stocker les données de l'utilisateur créé
      setCreatedUserData(result);
      // Afficher le modal avec le mot de passe
      setShowPasswordModal(true);
      showSuccess('Succès', 'Utilisateur créé avec succès !');
    } catch (error) {
      showError('Erreur', 'Impossible de créer l\'utilisateur');
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour vérifier si un site par défaut est requis selon le rôle sélectionné
  const isSiteRequiredForRole = (roleName: string): boolean => {
    const siteRequiredRoles = [
      'SITE_ADMIN', 
      'SITE_MANAGER', 
      'SITE_SUPERVISOR', 
      'STORE_MANAGER', 
      'VIEWER', 
      'ADMIN', 
      'MANAGER', 
      'OPERATOR'
    ];
    return siteRequiredRoles.includes(roleName);
  };

  // Fonction pour vérifier si un tenant est requis selon le rôle sélectionné
  const isTenantRequiredForRole = (roleName: string): boolean => {
    const tenantRequiredRoles = [
      'SITE_ADMIN', 
      'SITE_MANAGER', 
      'SITE_SUPERVISOR', 
      'STORE_MANAGER', 
      'VIEWER', 
      'ADMIN', 
      'MANAGER', 
      'OPERATOR'
    ];
    return tenantRequiredRoles.includes(roleName);
  };

  // Fonction pour générer un libellé expressif pour un rôle
  const getRoleDisplayName = (role: Role): string => {
    // Si le rôle a déjà un label, l'utiliser
    if (role.label && role.label.trim()) {
      return role.label;
    }

    // Si le rôle a une description, l'utiliser
    if (role.description && role.description.trim()) {
      return role.description;
    }

    // Sinon, générer un libellé basé sur le nom du rôle
    const roleLabels: { [key: string]: string } = {
      'PLATFORM_SUPER_ADMIN': 'Super Administrateur de Plateforme',
      'PLATFORM_ADMIN': 'Administrateur de Plateforme',
      'PLATFORM_SUPPORT': 'Support Plateforme',
      'TENANT_OWNER': 'Propriétaire de Tenant',
      'TENANT_ADMIN': 'Administrateur de Tenant',
      'TENANT_MANAGER': 'Gestionnaire de Tenant',
      'SITE_ADMIN': 'Administrateur de Site',
      'SITE_MANAGER': 'Gestionnaire de Site',
      'SITE_SUPERVISOR': 'Superviseur de Site',
      'STORE_MANAGER': 'Gestionnaire de Magasin',
      'VIEWER': 'Consultant (Lecture seule)',
      'ADMIN': 'Administrateur',
      'MANAGER': 'Gestionnaire',
      'OPERATOR': 'Opérateur',
      'QUALITY_AGENT': 'Agent Qualité',
      'CONTROLLER': 'Contrôleur',
      'SUPERVISOR': 'Superviseur',
      'TECHNICIAN': 'Technicien',
      'AUDITOR': 'Auditeur',
      'COMMERCIAL': 'Commercial',
      'FINANCE': 'Finance',
      'STOCK': 'Stock'
    };

    return roleLabels[role.name] || role.name;
  };

  // Fonction pour générer un nom d'utilisateur basé sur le prénom et le nom
  const generateUsername = (prenom: string, nom: string) => {
    if (!prenom || !nom || prenom.trim().length < 2 || nom.trim().length < 2) return '';
    
    // Nettoyer et traiter le prénom (prendre le dernier s'il y en a plusieurs)
    const prenoms = prenom.trim().split(/\s+/).filter(p => p.length > 0);
    const dernierPrenom = prenoms[prenoms.length - 1] || '';
    const prenomClean = dernierPrenom.toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
    
    // Nettoyer le nom de famille
    const nomClean = nom.trim().toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
    
    if (prenomClean && nomClean) {
      return `${prenomClean}.${nomClean}`;
    }
    
    return '';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin/users')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <Users className="w-8 h-8 text-orange-500 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Créer un Utilisateur</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border bg-white/50 shadow-sm p-6">
          <Form
            schema={createUserSchema}
            onSubmit={handleSubmit}
            defaultValues={{
              username: '',
              email: '',
              prenom: '',
              nom: '',
              phone: '',
              locale: 'fr',
              role_id: '',
              tenant_id: '',
              default_site_id: ''
            }}
          >
            <CreateUserFormContent 
              filteredRoles={filteredRoles}
              filteredSites={filteredSites}
              allTenants={allTenants}
              isPlatformAdmin={isPlatformAdmin}
              sitesForSelectedTenant={sitesForSelectedTenant}
              setSitesForSelectedTenant={setSitesForSelectedTenant}
              isSiteRequiredForRole={isSiteRequiredForRole}
              isTenantRequiredForRole={isTenantRequiredForRole}
              getRoleDisplayName={getRoleDisplayName}
              generateUsername={generateUsername}
              isLoading={isLoading}
              onCancel={() => navigate('/admin/users')}
            />
          </Form>
        </div>
      </div>


      {/* Modal d'affichage du mot de passe généré */}
      <Dialog
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setCreatedUserData(null);
          navigate('/admin/users');
        }}
        title="Utilisateur créé avec succès"
        size="lg"
      >
        {createdUserData && (
          <div className="space-y-6">
            {/* Informations utilisateur */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-green-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">Utilisateur créé</h3>
                  <p className="text-sm text-green-700 mt-1">
                    <strong>{createdUserData.user.name}</strong> ({createdUserData.user.username})
                  </p>
                </div>
              </div>
            </div>

            {/* Mot de passe temporaire */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Shield className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Mot de passe temporaire</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Ce mot de passe doit être changé lors de la première connexion
                  </p>
                </div>
              </div>
              
              <div className="bg-white border border-blue-300 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <code className="text-lg font-mono text-gray-900 bg-gray-100 px-3 py-2 rounded border">
                    {createdUserData.tempPassword || 'Non généré'}
                  </code>
                  <button
                    onClick={() => {
                      if (createdUserData.tempPassword) {
                        navigator.clipboard.writeText(createdUserData.tempPassword);
                        showSuccess('Copié', 'Mot de passe copié dans le presse-papiers');
                      }
                    }}
                    disabled={!createdUserData.tempPassword}
                    className={`ml-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${
                      createdUserData.tempPassword 
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300' 
                        : 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                    }`}
                    title="Copier le mot de passe"
                  >
                    <Plus className="h-4 w-4" />
                    Copier
                  </button>
                </div>
              </div>
            </div>

            {/* Statut de l'email */}
            <div className={`border rounded-lg p-4 ${
              createdUserData.emailSent 
                ? 'bg-green-50 border-green-200' 
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center">
                <EnvelopeIcon className={`h-5 w-5 mr-3 ${
                  createdUserData.emailSent ? 'text-green-600' : 'text-yellow-600'
                }`} />
                <div>
                  <h3 className={`text-sm font-medium ${
                    createdUserData.emailSent ? 'text-green-800' : 'text-yellow-800'
                  }`}>
                    {createdUserData.emailSent ? 'Email envoyé' : 'Email non envoyé'}
                  </h3>
                  <p className={`text-sm mt-1 ${
                    createdUserData.emailSent ? 'text-green-700' : 'text-yellow-700'
                  }`}>
                    {createdUserData.emailSent 
                      ? 'Un email avec les informations de connexion a été envoyé à l\'utilisateur'
                      : 'L\'email n\'a pas pu être envoyé. Communiquez manuellement les informations de connexion'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <Lightbulb className="h-5 w-5 text-gray-600 mr-3 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-medium">Instructions importantes :</p>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>Communiquez ces informations de connexion à l'utilisateur de manière sécurisée</li>
                    <li>L'utilisateur devra changer son mot de passe lors de sa première connexion</li>
                    <li>Assurez-vous que l'utilisateur a bien reçu l'email (si envoyé)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setCreatedUserData(null);
                  navigate('/admin/users');
                }}
              className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
            >
                Retour à la liste
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setCreatedUserData(null);
                  // Réinitialiser le formulaire pour créer un autre utilisateur
                  window.location.reload();
                }}
                className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors"
              >
                Créer un autre utilisateur
            </button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Container des toasts */}
      <ToastContainer toasts={toasts} position="top-right" />
    </div>
  );
};

export default CreateUser;
